import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import {
  ApolloGqlTalentForTalentBoard,
  DealType,
  Maybe,
  TalentBoardEventType,
  TalentBoardPersistedEventType,
} from '@bloomays-lib/types.shared';
import { ProfileCard, HrefLink, Switch, Title } from '@bloomays-lib/ui.shared';
import { formatSpace } from '@bloomays-lib/utils.shared';
import { getPhaseColor } from '../../helpers/utils';
import { useTalentBoardEvents } from '../../hooks/useTalentBoardEvents';
import useUpdateMarkedAsSeen from '../../hooks/useUpdateMarkedAsSeen';
import { useTalentsForTalentBoardStore } from '../../stores/talentsForTalentBoardStore';
import { useTalentBoardStore } from '../../stores/talentBoardStore';
import TalentBoardCommentList from './TalentBoardCommentList';
import { Logger } from '../../services/service-logger';
import useJobList from '../../hooks/useJobList';
import SynchroTalentFromATSButton from '../molecules/SynchroTalentFromATSButton';

type TalentJobDataProps = {
  talent: ApolloGqlTalentForTalentBoard;
  jobName: string;
  jobId: string;
  isClient: boolean;
};

const logger = Logger('TalentJobData');

const TalentJobData = ({ talent, jobName, jobId, isClient }: TalentJobDataProps) => {
  const { t } = useTranslation('talentBoard');
  const { updateMarkedAsSeen } = useUpdateMarkedAsSeen();
  const { updateTalentMarkedAsSeen } = useTalentsForTalentBoardStore();
  const { updateSelectedTalentMarkedAsSeen } = useTalentBoardStore();
  const { getJobById } = useJobList();
  const { createTalentBoardEvent } = useTalentBoardEvents();

  const [disabledSwitchState, setDisabledSwitchState] = useState(false);
  const hasCalledUpdateRef = useRef<string | null>(null);

  const job = getJobById(jobId);

  const isCDI = Boolean(job?.dealType) && [DealType.CDI_PEOPLE, DealType.CDI_TECH].includes(job?.dealType as DealType);

  const formatTalentPrice = (value: Maybe<string>): string => {
    if (!value) {
      return t('talentJob.toConfirm');
    }

    const normalized = formatSpace(value);

    if (/^\d+$/.test(normalized)) {
      return `${value} €`;
    }

    return value;
  };

  const { id: talentId, markedAsSeen } = talent || {};
  useEffect(() => {
    if (!isClient) {
      return;
    }

    setDisabledSwitchState(true);
    if (talentId && !markedAsSeen && hasCalledUpdateRef.current !== talentId) {
      hasCalledUpdateRef.current = talentId;
      const dateMarkedAsSeen = new Date();
      const talentName = talent?.firstname + ' ' + talent?.lastname;
      void updateMarkedAsSeen(talentId, talentName, jobId, jobName, true).then((success) => {
        if (success) {
          updateTalentMarkedAsSeen(jobId, talentId, true, dateMarkedAsSeen);
          updateSelectedTalentMarkedAsSeen(true, dateMarkedAsSeen);
        } else {
          hasCalledUpdateRef.current = null;
        }
        setDisabledSwitchState(false);
      });
    } else {
      setDisabledSwitchState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId, jobId]);

  const handleToggleMarkedAsSeen = async () => {
    setDisabledSwitchState(true);
    const success = await updateMarkedAsSeen(
      talent.id,
      talent.firstname + ' ' + talent.lastname,
      jobId,
      jobName,
      !talent.markedAsSeen,
    );
    if (success) {
      logger.info('Traffic', {
        page: 'TalentJobData',
        evt: {
          name: !talent.markedAsSeen
            ? TalentBoardEventType.TalentboardTalentMarkedAsSeen
            : TalentBoardEventType.TalentboardTalentMarkedAsNotSeen,
        },
        job: {
          id: jobId,
          name: jobName,
        },
        talent: {
          id: talent.id,
          name: talent.firstname + ' ' + talent.lastname,
        },
      });
      if (talent.markedAsSeen) {
        void createTalentBoardEvent({
          jobId,
          date: new Date(),
          type: TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen,
          data: { talentId: talent.id, talentFullName: talent.firstname + ' ' + talent.lastname },
        });
      }
      const dateMarkedAsSeen = new Date();
      updateTalentMarkedAsSeen(jobId, talent.id, !talent.markedAsSeen, dateMarkedAsSeen);
      updateSelectedTalentMarkedAsSeen(!talent.markedAsSeen, dateMarkedAsSeen);
      setDisabledSwitchState(false);
    }
  };

  return (
    <Container isClient={isClient}>
      {isClient && (
        <SwitchWrapper>
          <SwitchLabel>{t('talentJob.markAsNotSeen')}</SwitchLabel>
          <Switch
            disabled={disabledSwitchState}
            checked={!talent.markedAsSeen}
            onSwitch={() => handleToggleMarkedAsSeen()}
          />
        </SwitchWrapper>
      )}
      <ProfileCard
        name={`${talent.firstname} ${talent.lastname}`}
        jobTitle={talent.jobTitle}
        size="large"
        image={talent.image || ''}
      />
      <InfoLine>
        <InfoLabel>{t('talentJob.job')}:</InfoLabel>
        <InfoValue>{jobName}</InfoValue>
      </InfoLine>
      <InfoLine>
        <InfoLabel>{t('talentJob.actualPhase')}:</InfoLabel>
        <PhaseCell color={getPhaseColor(talent.phase)}>{t(`jobFunnelPhases.${talent.phase}`)}</PhaseCell>
      </InfoLine>
      <InfoLine>
        <InfoLabel>{t('talentJob.email')}:</InfoLabel>
        <HrefLine href={`mailto:${talent.email}`}>{talent.email}</HrefLine>
      </InfoLine>
      <InfoLine>
        <InfoLabel>{isCDI ? t('kpis.package') : t('kpis.tjm')}:</InfoLabel>
        {isCDI ? (
          <InfoValue>{formatTalentPrice(talent.package)}</InfoValue>
        ) : (
          <InfoValue>{formatTalentPrice(talent.tjm)}</InfoValue>
        )}
      </InfoLine>
      <InfoLine>
        <InfoLabel>{t('talentJob.networks')}:</InfoLabel>
        <NetworksLinks>
          {talent.profiles?.map((profile, index) => (
            <span key={index}>
              <HrefLink href={profile.url || ''} target="_blank" sx={{ display: 'inline-flex' }}>
                {profile.network}
              </HrefLink>
              {index < (talent.profiles?.length || 0) - 1 && <CommaSeparator>, </CommaSeparator>}
            </span>
          ))}
        </NetworksLinks>
      </InfoLine>
      <Separator />
      {!isClient && (
        <>
          <SynchroTalentFromATSButton talent={talent} jobId={jobId} />
          <Separator />
        </>
      )}
      {talent.cardTamComment && (
        <>
          <Title textTitle={t('talentJob.cardTamComment')} variant="heading6" sx={{ textAlign: 'left' }} />
          <CommentValue>{talent.cardTamComment}</CommentValue>
          <Separator />
        </>
      )}
      <TalentBoardCommentList isClient={isClient} comments={talent.comments} jobId={jobId} talentId={talentId} />
    </Container>
  );
};

const Container = styled('div', { shouldForwardProp: (prop) => prop !== 'isClient' })<{ isClient: boolean }>(
  ({ theme, isClient }) => ({
    width: '100%',
    padding: isClient ? '0px 24px' : '30px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      padding: '0px 16px',
    },
  }),
);

const SwitchWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '28px',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',

  [theme.breakpoints.down('md')]: {
    marginTop: '12px',
  },
}));

const SwitchLabel = styled('span')(({ theme }) => ({
  color: theme.palette.neutral.black,
  fontFamily: 'Poppins Medium',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '19.6px',
}));

const InfoLine = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const InfoLabel: React.FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '14px',
}));

const InfoValue = styled('span')(() => ({
  fontFamily: 'Poppins Medium',
  fontWeight: '600',
  fontSize: '14px',
}));

const NetworksLinks = styled('span')(() => ({
  fontFamily: 'Poppins Medium',
  fontWeight: '600',
  fontSize: '14px',
  display: 'inline',
  '& > span': {
    display: 'inline',
  },
}));

const CommaSeparator = styled('span')(() => ({
  fontFamily: 'Poppins Regular',
  fontWeight: '400',
  fontSize: '14px',
}));

const HrefLine = styled('a')(() => ({
  fontSize: '14px',
}));

const Separator = styled('div')(({ theme }) => ({
  margin: '12px 0',
  borderBottom: `1px solid ${theme.palette.neutral[20]}`,
  width: '100%',
}));

const CommentValue: React.FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '14px',
}));

const PhaseCell = styled('div', { shouldForwardProp: (prop) => prop !== 'color' })<{ color: string }>(
  ({ theme, color }) => ({
    fontSize: '14px',
    padding: '4.5px 16px',
    borderRadius: '5px',
    backgroundColor: color,
  }),
);

export { TalentJobData };
export default TalentJobData;
